import {GRADE_LABELS} from '../constants'

/**
 * Phần tính toán thuần của nhóm cột HIỆU SUẤT trên hai báo cáo M18 (FAC-26417 FR-003).
 *
 * Tách khỏi component vì **trong repo này không file test nào import một component nào** — mọi logic
 * nằm trong `.js` của màn hình đều có mutation score 0 theo cấu tạo. Cùng lý do đã ghi cho
 * `helpers/actualHours.js` và `helpers/timesheetImport.js`.
 *
 * ⚠️ Mọi con số hiệu suất ở đây là PHÂN SỐ (0,843), không phải phần trăm — khớp service. Nơi duy
 *    nhất nhân 100 là lúc hiển thị.
 */

/**
 * Câu "số liệu mới đủ tới đâu" — `null` khi đủ tới hết khoảng lọc, tức KHÔNG hiện gì.
 *
 * ⚠️ Service chỉ đặt `dataFreshAs` khi khoảng lọc chứa ngày CHƯA KẾT THÚC và lượt đọc đi qua bảng
 *    tổng hợp. Bảng chỉ được job nền làm tươi mỗi `OUTPUT_ROLLUP_CYCLE_MS` (mặc định 5 phút), nên
 *    sản lượng từ mốc này tới bây giờ CHƯA có trong con số đang hiện. Không nói ra thì người dùng
 *    đọc "hôm nay làm được N" và tưởng đó là số cuối cùng — và hai người mở cùng một màn ở hai
 *    thời điểm thấy hai con số khác nhau mà không ai giải thích được.
 *
 * ⚠️ Vắng mặt (`null`/`undefined`) nghĩa là ĐỦ, không phải "không biết": đường đọc thường lấy log
 *    trực tiếp nên nó đủ tới đúng lúc bấm, và BE bản cũ cũng không có field này. Suy ngược lại —
 *    coi vắng mặt là "có thể thiếu" — là dán một cảnh báo vĩnh viễn lên mọi báo cáo.
 *
 * Trả về `{time, minutes}`: `time` là giờ:phút để hiện, `minutes` là độ cũ tính bằng phút (dùng
 * cho câu phụ). Chuỗi hiển thị dựng ở component, không dựng ở đây — helper này phải test được mà
 * không cần moment locale.
 */
export const freshnessOf = (dataFreshAs, now = Date.now()) => {
    if (!dataFreshAs) return null

    const at = new Date(dataFreshAs)

    if (Number.isNaN(at.getTime())) return null

    return {at, minutes: Math.max(0, Math.round((now - at.getTime()) / 60000))}
}

/** Hạng của một giá trị hiệu suất theo ngưỡng đang cấu hình. `null` khi chưa đo được. */
export const gradeKeyOf = (efficiency, thresholds) => {
    if (efficiency === null || efficiency === undefined || !thresholds) return null
    if (efficiency >= thresholds.a) return 'A'
    if (efficiency >= thresholds.b) return 'B'
    if (efficiency >= thresholds.c) return 'C'

    return 'D'
}

/**
 * Nhãn + màu của một hạng.
 *
 * ⚠️ Hạng `null` KHÔNG phải hạng D. Chưa đủ điều kiện xếp hạng thì hiện "Chưa xếp hạng" kèm lý do;
 *    đổ về D là chấm điểm người ta bằng dữ liệu không đủ để chấm, và họ sẽ không tin cả báo cáo.
 */
export const gradeLabelOf = (grade) => GRADE_LABELS[grade] || GRADE_LABELS.N

/**
 * Chữ in trên thẻ hạng — LUÔN có cả chữ cái lẫn nghĩa (BR-3.3, AC-UI-01).
 *
 * ⚠️ Hàm riêng chứ không nối chuỗi tại chỗ, vì đây là một luật chứ không phải một cách trình bày:
 *    màu KHÔNG được là kênh duy nhất phân biệt bốn hạng. Bản trước in mỗi "A" và để "Tốt" trong
 *    tooltip — mất hẳn khi in ra giấy, và gần như mất trên màn hình xưởng độ tương phản thấp.
 */
export const gradeTagTextOf = (grade) => {
    const label = gradeLabelOf(grade)

    return `${label.text} · ${label.title}`
}

/**
 * Chênh lệch này là tin TỐT hay tin XẤU.
 *
 * ⚠️ Không suy được từ dấu. Hiệu suất tăng là tốt; số người hạng D tăng là xấu. Bản trước tô màu
 *    thuần theo dấu nên "▲5" người hạng D hiện màu xanh thành công — màn hình chúc mừng đúng lúc
 *    xưởng có thêm năm người cần hỗ trợ.
 */
export const deltaToneOf = (value, invert = false) => {
    /*
     * ⚠️ Gác theo con số SẼ IN RA, không theo giá trị thô. Chip in `|delta| × 100` với 1 chữ số,
     *    nên mọi chênh lệch dưới 0,0005 hiện ra là "▲0,0 điểm" — một mũi tên có màu cho một thay
     *    đổi bằng không. `=== 0` không bắt được vì `deltaOf` trả hiệu của hai số thực.
     */
    if (value === null || value === undefined) return null
    if (Math.abs(value) * 100 < 0.05) return null

    const up = value > 0

    return (invert ? !up : up) ? 'VcSuccess' : 'VcDanger'
}

/**
 * Tông màu của một ô hiệu suất, dùng chung bảng màu với hạng.
 *
 * Trả về KHOÁ hạng chứ không trả mã màu: bảng màu nằm ở `constants`, và rải mã hex vào component là
 * cách chắc chắn nhất để hai màn hiện hai màu khác nhau cho cùng một ngưỡng.
 */
export const efficiencyToneOf = (efficiency, thresholds) => gradeKeyOf(efficiency, thresholds)

/**
 * Nhãn khoảng ngày của KỲ TRƯỚC — "02/08 – 31/08", hoặc kèm năm khi kỳ đó không cùng năm với nhau.
 *
 * ⚠️ Đọc `totals.previous.range` do service trả, KHÔNG tự tính. Luật kỳ trước là "cùng độ dài, lùi
 *    1 ms, tính từ khoảng ĐÃ chuẩn hoá" — FE không có khoảng đã chuẩn hoá (server còn kéo `to`
 *    date-only tới cuối ngày), nên một bản dịch thứ hai ở đây sẽ lệch đúng một ngày, và lệch im
 *    lặng. Đây chính là loại lệch mà `isEffectiveAt` phải dựng hai file contract test để canh.
 *
 * Trả `null` khi chưa có số kỳ trước — chỗ gọi tự quyết hiện gì, đừng trả chuỗi rỗng.
 */
export const previousRangeLabelOf = (totals) => {
    const range = totals && totals.previous && totals.previous.range

    if (!range || !range.from || !range.to) return null

    // `YYYY-MM-DD` → `DD/MM`. Cắt chuỗi chứ không qua `Date`: chuỗi ở đây ĐÃ là ngày lịch VN do
    // service quy đổi, còn `new Date('2026-08-02')` đọc theo UTC rồi `getDate()` trả theo giờ máy.
    const short = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`
    const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4)

    return sameYear
        ? `${short(range.from)} – ${short(range.to)}/${range.to.slice(0, 4)}`
        : `${short(range.from)}/${range.from.slice(0, 4)} – ${short(range.to)}/${range.to.slice(0, 4)}`
}

/**
 * Dòng phụ "thuần ..." dưới ô Hiệu suất — Hiệu suất thuần của FR-003 (PO câu 12).
 *
 * Trả `null` nghĩa là ĐỪNG VẼ dòng phụ, và có ba lý do khác nhau cùng cho `null`:
 *   · không có khối hiệu suất, hoặc ô "Không áp dụng" — ô đó đã có chữ riêng rồi;
 *   · chưa đo được hiệu suất thuần (cùng mẫu số nên nó `null` đúng lúc hiệu suất chính `null`);
 *   · hai con số LÀM TRÒN RA BẰNG NHAU.
 *
 * ⚠️ Vế thứ ba là chủ đích, không phải tiết kiệm chỗ. Dòng phụ chỉ có giá trị ở chỗ nó KHÁC con số
 *    phía trên; in "thuần 86%" ngay dưới "86%" trên hàng trăm dòng là bắt người đọc quét qua một
 *    dòng chữ không mang tin nào. Công đoạn đếm theo ĐƠN luôn bằng nhau theo cấu tạo (mọi record
 *    `seq = 1`), nên không gác thì cả cột Dán label và Đóng thùng đều lặp số.
 *    Việc "có làm lại hay không" KHÔNG bị giấu đi: cột Số lần làm lại nằm ngay trên cùng một hàng.
 *
 * ⚠️ So theo số ĐÃ LÀM TRÒN, không so `pure < efficiency`. Chênh 0,2 điểm là hai ô hiện y hệt nhau,
 *    và một dòng phụ lặp lại đúng con số phía trên trông như lỗi hiển thị.
 */
export const pureEfficiencyNoteOf = (eff, digits = 1) => {
    if (!eff || eff.notApplicable) return null

    const {efficiency, efficiencyPure} = eff

    if (typeof efficiencyPure !== 'number' || typeof efficiency !== 'number') return null

    /*
     * ⚠️ Mặc định MỘT chữ số, khớp `formatEfficiency` (BR-3.2) — đây là chỗ quyết định dòng phụ
     *    "thuần" có hiện hay không, nên nó phải so ở ĐÚNG độ chính xác mà người dùng nhìn thấy.
     *    Để 0 chữ số thì 86,2% / thuần 85,8% bị coi là bằng nhau và dòng phụ biến mất, trong khi
     *    hai con số in ra khác nhau; ngược lại 86,54% / 86,46% cùng in "86,5%" mà vẫn hiện hai
     *    dòng giống hệt nhau.
     */
    const round = (value) => Number((value * 100).toFixed(digits))

    if (round(efficiencyPure) === round(efficiency)) return null

    return {
        value: efficiencyPure,
        // Chênh tính trên số THẬT chứ không trên số đã làm tròn — câu giải thích không nên tự mâu
        // thuẫn với chính nó khi người dùng lấy hai số trên màn hình trừ cho nhau.
        gapPoints: (efficiency - efficiencyPure) * 100,
    }
}

/**
 * Chỉ số tương đối → tông màu.
 *
 * Ngưỡng ở đây CỐ Ý khác ngưỡng hiệu suất: 1,00 là bằng mặt bằng, nên mốc đáng chú ý là quanh 1 chứ
 * không phải 0,9/0,75/0,6. Dùng chung ngưỡng hiệu suất thì gần như mọi người đều hiện màu đỏ, vì
 * chỉ số tương đối hiếm khi vượt 1,3.
 */
export const relativeToneOf = (relative) => {
    if (relative === null || relative === undefined) return null
    if (relative >= 1.1) return 'A'
    if (relative >= 0.9) return 'B'
    if (relative >= 0.75) return 'C'

    return 'D'
}

/**
 * Cặp "Dưới ngưỡng" của một công đoạn: `below` trên `scopedSampleSize`.
 *
 * ⚠️ Mẫu số là NHÓM ĐANG LỌC (BR-5.3), KHÔNG phải `sampleSize` (tập mốc toàn xưởng, BR-7.2). Ghép
 *    nhầm hai bên ra `3/47` trên một bảng 5 dòng — một phân số của hai dân số khác nhau, không
 *    mang nghĩa gì. Đã sai một lần, và sửa nửa vời lần nữa vì cặp số này nằm RỜI ở hai file.
 *
 * Gom vào một hàm để hai màn không thể lệch nhau lần thứ ba. `null` = chưa có ai trong nhóm lọc
 * đủ mẫu, màn hình để gạch ngang.
 */
export const belowRatioOf = (dist) => {
    if (!dist || !dist.scopedSampleSize) return null

    return {below: dist.below, total: dist.scopedSampleSize}
}

/**
 * Chú thích đi sau con số giờ ở cột "Giờ làm thực tế".
 *
 * ⚠️ Dòng CHA đi với `enteredDayCount` (số ngày ĐÃ NHẬP giờ), KHÔNG với `dayCount` (số ngày PHÂN
 *    BỔ ĐƯỢC). Thợ chỉ làm In/Cắt/Khắc có `actualHours = 176` nhưng `dayCount = 0` vì `by_detect`
 *    chưa quy đổi được giờ ⇒ ô in "176,0 h · 0 ngày", hai nửa của cùng một ô nói ngược nhau.
 *
 * Dòng CON là giờ PHÂN BỔ cho một công đoạn — phải nói ra, nếu không người đọc cộng các dòng con
 * lại, thấy không bằng dòng cha, rồi báo lỗi số liệu.
 */
export const hoursNoteOf = (isParentRow, eff) => {
    if (!isParentRow) return 'phân bổ'
    if (!eff || typeof eff.enteredDayCount !== 'number') return null

    return `${eff.enteredDayCount} ngày`
}

/**
 * Vì sao báo cáo không có con số hiệu suất nào — câu đi vào chú thích thẻ KPI.
 *
 * ⚠️ BA nguyên nhân cho cùng một ô trống, và chúng dẫn người dùng đi ba hướng khác hẳn nhau. Bản
 *    trước chỉ có một câu ("chưa ai nhập giờ làm"), nên khi service lỗi đọc `pp_actual_hours` thì
 *    thẻ KPI đổ lỗi cho người nhập chấm công — ngay phía trên một dải đỏ ghi "đừng nhập lại chấm
 *    công". Hai câu nói ngược nhau, và cái người dùng đọc trước là cái sai.
 *
 * Thứ tự xét là thứ tự NGƯỜI DÙNG LÀM ĐƯỢC GÌ: lỗi hệ thống thì báo kỹ thuật, khoảng không hợp lệ
 * thì chọn lại ngày, còn lại mới là đi nhập giờ.
 */
export const noEfficiencyReasonOf = (report) => {
    if (report && report.hoursUnavailable) {
        return 'lỗi đọc dữ liệu giờ làm — xem thông báo bên dưới, đừng nhập lại chấm công'
    }

    if (report && report.efficiencyDays === 0) {
        return 'khoảng đang chọn không chứa trọn một ngày nào — chọn lại theo ngày'
    }

    return 'chưa ai nhập giờ làm — mở màn Giờ làm thực tế để nhập'
}

/**
 * Công đoạn vướng MỐC dữ liệu trong khoảng đang lọc (EC-B-05) — nguồn của Notice màu xanh.
 *
 * ⚠️ Lọc bỏ công đoạn `notApplicable`: câu của nhóm đó là "không bao giờ đo được" (Khắc), đã hiện
 *    bằng chữ "Không áp dụng" ngay trong ô. Gộp hai loại vào một thông báo là trộn "chưa tới lúc"
 *    với "không bao giờ" — và `personalEfficiencyBlocker` của service xét `unavailable` TRƯỚC
 *    `since`, nên cả hai loại đều về đây với một câu `blockerReason` khác `null`.
 *
 * Ở `helpers/` chứ không trong component vì đây là luật CHỌN, và trong repo này không file test nào
 * import một component nào.
 */
export const milestoneStagesOf = (stageSignals) =>
    (stageSignals || []).filter((stage) => !stage.notApplicable && stage.blockerReason)

/**
 * Hình học của hộp phân bố (FR-005): co thang về `[P10, P90]` rồi trả vị trí theo phần trăm.
 *
 * ⚠️ Mọi người bằng nhau tuyệt đối (`p10 === p90`) thì không có thang để co — trả `null` để người
 *    vẽ biết là KHÔNG vẽ hộp, thay vì dồn mọi vạch về giữa rồi để người đọc đoán xem đó là chủ đích
 *    hay lỗi.
 *
 * `target` là mốc 100% — mốc ĐỊNH MỨC, không thuộc phân bố. `null` khi nó nằm ngoài [P10, P90]:
 * lúc đó cả công đoạn lệch hẳn một phía, và đó đã là việc của cột Tín hiệu.
 */
export const spreadGeometryOf = (dist) => {
    if (!dist || dist.p10 === null || dist.p90 === null) return null

    const lo = dist.p10
    const hi = dist.p90
    const span = hi - lo

    if (span <= 0) return null

    const pos = (value) => ((value - lo) / span) * 100
    const hasBox = dist.p25 !== null && dist.p25 !== undefined && dist.p75 !== null && dist.p75 !== undefined

    return {
        lo,
        hi,
        box: hasBox ? {left: pos(dist.p25), width: Math.max(pos(dist.p75) - pos(dist.p25), 2)} : null,
        median: dist.median === null || dist.median === undefined ? null : pos(dist.median),
        target: lo <= 1 && hi >= 1 ? pos(1) : null,
    }
}

/**
 * Công đoạn có độ phủ ĐỊNH MỨC dưới ngưỡng — hiệu suất ở đó phần lớn là ước tính (FR-008, BR-8.3).
 *
 * ⚠️ Đây là cảnh báo PO ra điều kiện mới cho ra mắt (câu 25): dưới ngưỡng thì con số "không dùng
 *    tính thưởng/lương". Không có nó, một công đoạn mới phủ 40% định mức vẫn hiện hiệu suất như
 *    mọi công đoạn khác, và con số đó đi thẳng vào bảng lương.
 *
 * Gom từ `groups[].children[]` nên chạy ĐÚNG trên cả hai màn: phần tử con luôn là (tài khoản ×
 * công đoạn) dù trang đang gộp theo trục nào — cùng khuôn với `redoBreakdown` của trang.
 *
 * Độ phủ tính lại từ SẢN LƯỢNG (`1 − thiếu ÷ tổng`), không lấy trung bình các tỷ lệ con: trung bình
 * của tỷ lệ coi một người làm 5 sản phẩm nặng bằng một người làm 5.000.
 */
export const lowCoverageStagesOf = (groups, threshold) => {
    if (!threshold) return []

    const byStage = new Map()

    for (const group of groups || []) {
        for (const child of group.children || []) {
            const acc = byStage.get(child.stageCode) || {
                stageCode: child.stageCode,
                name: child.name || child.stageCode,
                output: 0,
                noStandardOutput: 0,
            }

            acc.output += child.output || 0
            acc.noStandardOutput += child.noStandardOutput || 0
            byStage.set(child.stageCode, acc)
        }
    }

    return [...byStage.values()]
        // Công đoạn chưa có sản lượng nào thì chưa có gì để nói — `0/0` không phải "phủ 0%".
        .filter((stage) => stage.output > 0)
        .map((stage) => ({...stage, coverage: 1 - stage.noStandardOutput / stage.output}))
        .filter((stage) => stage.coverage < threshold)
        .sort((a, b) => a.coverage - b.coverage)
}

/**
 * Công đoạn có trung vị lệch khỏi khoảng hợp lý — nhiều khả năng sai ở ĐỊNH MỨC, không ở người.
 *
 * ⚠️ Đọc `report.stageSignals` (service trả cho CẢ HAI màn), KHÔNG đọc `report.stages`. Màn theo
 *    nhân viên không có `stages[]`, nên bản trước gác bằng `byStage &&` và cảnh báo này chỉ tồn
 *    tại ở một trong hai màn — trong khi nó nói về dữ liệu cả hai màn đang hiện.
 */
export const rateSanityWarningsOf = (stageSignals) => (stageSignals || [])
    .filter((s) => s.rateSanity === 'too_high' || s.rateSanity === 'too_low')
    .map((s) => ({stageCode: s.stageCode, name: s.name, median: s.median, direction: s.rateSanity}))

/**
 * Phân bố tài khoản theo hạng — số liệu của khối "Tổng quan hiệu suất đội ngũ".
 *
 * ⚠️ Đếm cả nhóm `N` (chưa xếp hạng) và hiện nó ra. Giấu đi thì tổng bốn hạng không bằng số tài
 *    khoản trên bảng, và người đọc sẽ đi tìm phần chênh.
 */
export const gradeBreakdownOf = (actors) => {
    const counts = {A: 0, B: 0, C: 0, D: 0, N: 0}

    ;(actors || []).forEach((a) => {
        const eff = a.efficiency

        if (!eff || eff.notApplicable) return

        const key = eff.grade && eff.grade.grade ? eff.grade.grade : 'N'

        counts[key] += 1
    })

    return counts
}

/**
 * Tài khoản CẦN CHÚ Ý — tối đa `limit` người, xếp hiệu suất thấp trước.
 *
 * ⚠️ Chỉ lấy người ĐÃ xếp hạng được. Người chưa đủ dữ liệu mà bị đẩy lên đầu danh sách "cần chú ý"
 *    là tố cáo họ bằng một con số chính hệ thống nói là chưa đáng tin.
 *
 * Tài khoản hệ thống loại hẳn — nó không phải một con người.
 */
export const attentionListOf = (actors, thresholds, limit = 5) => (actors || [])
    .filter((a) => {
        const eff = a.efficiency

        if (!eff || eff.notApplicable || eff.efficiency === null) return false
        if (!eff.grade || !eff.grade.grade) return false

        return eff.efficiency < (thresholds ? thresholds.b : 0.75)
    })
    .sort((a, b) => a.efficiency.efficiency - b.efficiency.efficiency)
    .slice(0, limit)

/**
 * Ma trận tài khoản × công đoạn.
 *
 * ⚠️ Chỉ dựng khi người dùng ĐÃ thu hẹp (chọn nhóm tài khoản hoặc một công đoạn) — xem
 *    `shouldShowMatrix`. Dựng cho toàn xưởng là một lưới 200 × 9 không ai đọc được, và nó đẩy phần
 *    còn lại của trang xuống dưới màn hình.
 */
export const matrixOf = (actors, stageCodes, focusStageCode = null) => {
    const codes = stageCodes || []
    const rows = (actors || [])
        .filter((a) => a.efficiency && !a.efficiency.notApplicable)
        .map((a) => ({
            actor: a.actor,
            total: a.efficiency,
            cells: codes.map((code) => {
                const stage = (a.stages || []).find((s) => s.stageCode === code)

                return {stageCode: code, efficiency: stage && stage.efficiency ? stage.efficiency : null}
            }),
        }))

    /*
     * Lọc đúng MỘT công đoạn thì xếp theo chỉ số tương đối TẠI công đoạn đó (FR-007) — câu hỏi lúc
     * ấy là "ai mạnh nhất ở công đoạn này", và một lưới theo thứ tự chèn không trả lời được nó.
     * `null` xuống cuối: chưa đo được KHÁC với đo được và thấp.
     */
    if (!focusStageCode) return rows

    const indexAt = (row) => {
        const cell = row.cells.find((c) => c.stageCode === focusStageCode)

        return cell && cell.efficiency ? cell.efficiency.relativeIndex : null
    }

    return [...rows].sort((a, b) => {
        const ia = indexAt(a)
        const ib = indexAt(b)

        if (ia === null && ib === null) return a.actor.localeCompare(b.actor)
        if (ia === null) return 1
        if (ib === null) return -1

        return ib - ia || a.actor.localeCompare(b.actor)
    })
}

/**
 * Tông của một ô ma trận — NĂM mức theo chỉ số tương đối (FR-007), không phải bốn mức theo hạng.
 *
 * ⚠️ Khác hẳn `efficiencyToneOf`, và đó là cả điểm của ma trận. Ô ma trận trả lời "người này so với
 *    MẶT BẰNG của chính công đoạn đó thì thế nào", nên một công đoạn có định mức đặt chặt sẽ không
 *    còn đỏ nguyên cột — bản trước tô theo hiệu suất tuyệt đối nên đúng là như vậy, và một cột đỏ
 *    toàn bộ không xếp được người nào với người nào.
 *
 * Năm mức dùng lại ĐÚNG bốn màu hạng đang có cộng một mức TRUNG TÍNH ở giữa ("ngang mặt bằng").
 * Không thêm màu thứ năm: bảng màu của M18 đã chốt, và một tông mới chỉ sống ở một lưới thì người
 * đọc phải học thêm một bảng chú giải cho một chỗ.
 */
export const matrixToneOf = (relative) => {
    if (relative === null || relative === undefined) return null
    if (relative >= 1.15) return 'A'
    if (relative >= 1.05) return 'B'
    if (relative >= 0.95) return 'N'
    if (relative >= 0.85) return 'C'

    return 'D'
}

/** Chữ đi kèm từng mức — màu không bao giờ là kênh duy nhất (AC-UI-01). */
export const MATRIX_TONE_WORDS = {
    A: 'vượt mặt bằng rõ',
    B: 'trên mặt bằng',
    N: 'ngang mặt bằng',
    C: 'dưới mặt bằng',
    D: 'kém hơn mặt bằng rõ',
}

/**
 * Ma trận có đáng hiện không.
 *
 * Bản mẫu chốt: chỉ hiện khi đã lọc nhóm tài khoản HOẶC một công đoạn. Đây không phải chuyện hiệu
 * năng mà là chuyện đọc được — ma trận chỉ có nghĩa khi so vài người với nhau trên vài công đoạn.
 */
export const shouldShowMatrix = (filters) => {
    if (!filters) return false

    const actors = filters.actors || []
    const stages = filters.stageCodes || []

    return actors.length > 0 || stages.length === 1 || !!filters.stageCode
}

/**
 * Chênh lệch so với kỳ trước, dạng phân số. `null` khi một trong hai kỳ chưa đo được.
 *
 * Trả HIỆU chứ không trả tỷ lệ: hiệu suất vốn đã là một tỷ số, và tỷ lệ của tỷ lệ là con số không
 * ai đọc được. "Tăng 3 điểm phần trăm" đọc được; "tăng 4,1%" thì phải hỏi lại 4,1% của cái gì.
 */
export const deltaOf = (current, previous) => {
    if (current === null || current === undefined) return null
    if (previous === null || previous === undefined) return null

    return current - previous
}

/**
 * Đường xu hướng (sparkline) — đổi chuỗi hiệu suất theo ngày thành toạ độ SVG.
 *
 * Trả về NHIỀU đoạn chứ không một đường liền: ngày chưa nhập giờ là `null`, và nối thẳng qua nó là
 * vẽ ra một đoạn dốc không có thật giữa hai ngày cách nhau cả tuần. Đứt quãng mới là sự thật.
 *
 * ⚠️ Trục dọc co theo min–max của CHÍNH chuỗi đó, nên hai đường của hai người KHÔNG so trực tiếp
 *    được với nhau bằng mắt. Đó là chủ đích: sparkline trả lời "người này đang lên hay xuống", còn
 *    "ai cao hơn ai" đã có cột Hiệu suất ngay bên cạnh trả lời bằng con số.
 */
/** Biên độ TỐI THIỂU của trục dọc, tính bằng phân số hiệu suất (0,30 = 30 điểm phần trăm). */
export const SPARK_MIN_SPAN = 0.3

export const sparkSegmentsOf = (trend, {width = 84, height = 22, pad = 2} = {}) => {
    const points = (trend || []).filter((p) => p && typeof p.efficiency === 'number')

    if (points.length === 0) return {segments: [], dots: [], last: null, min: null, max: null}

    const values = points.map((p) => p.efficiency)
    const min = Math.min(...values)
    const max = Math.max(...values)
    const n = (trend || []).length
    const stepX = n > 1 ? (width - pad * 2) / (n - 1) : 0

    /*
     * ⚠️ Trục dọc có BIÊN ĐỘ SÀN, không co sát min–max.
     *
     * Co sát là cách chắc chắn nhất để mọi đường trông như răng cưa: người dao động 70–75% được
     * kéo căng hết khung y hệt người dao động 3–281%, nên hình của hai người giống nhau trong khi
     * câu chuyện khác hẳn. Đo prod tháng 9, 10 dòng đầu: biên độ thật trải từ 47 điểm
     * (`thuytt_htvibe` 11–47%) tới 278 điểm (`duynv_dgvibe` 3–281%).
     *
     * Sàn 30 điểm nghĩa là: dao động nhỏ hơn thế thì hiện ra ĐÚNG mức nhỏ của nó, còn lớn hơn thì
     * vẫn co theo dữ liệu. Giữ tâm ở giữa khoảng thật để đường không bị dán lên mép.
     */
    const center = (min + max) / 2
    const half = Math.max((max - min) / 2, SPARK_MIN_SPAN / 2)
    const lo = center - half
    const span = half * 2
    const yOf = (v) => pad + (height - pad * 2) * (1 - (v - lo) / span)

    const segments = []
    /**
     * Điểm đứng MỘT MÌNH giữa hai khoảng trống.
     *
     * ⚠️ Phải vẽ thành CHẤM, không được bỏ. Bản trước chỉ nhận nhóm từ 2 điểm (`length > 1`) nên
     *    một ngày có số mà hai ngày kề đều trống thì biến mất hẳn khỏi hình — đo prod tháng 9:
     *    9 điểm bị mất trên 132 điểm của 10 dòng đầu. Dữ liệu có thật mà hình vẽ nuốt đi là tệ hơn
     *    một hình xấu.
     */
    const dots = []
    let current = []

    const flush = () => {
        if (current.length > 1) segments.push(current)
        else if (current.length === 1) dots.push(current[0])

        current = []
    }

    ;(trend || []).forEach((p, i) => {
        if (!p || typeof p.efficiency !== 'number') {
            flush()

            return
        }

        current.push({x: pad + stepX * i, y: yOf(p.efficiency), day: p.day, efficiency: p.efficiency})
    })

    flush()

    // Mốc CUỐI là điểm cuối cùng đo được, dù nó thuộc một đoạn hay đứng một mình.
    const tail = [...segments.flat(), ...dots].sort((a, b) => a.x - b.x).pop() ?? null

    return {segments, dots, last: tail, min, max}
}

/**
 * Hướng của đường: so điểm ĐẦU với điểm CUỐI đo được.
 *
 * Dùng hai đầu chứ không dùng độ dốc hồi quy: người đọc nhìn sparkline cũng chỉ so hai đầu, và một
 * con số nói ngược với hình vẽ là thứ phá niềm tin vào cả bảng.
 */
export const trendDirectionOf = (trend) => {
    const points = (trend || []).filter((p) => p && typeof p.efficiency === 'number')

    if (points.length < 2) return null

    const delta = points[points.length - 1].efficiency - points[0].efficiency

    // Dưới 1 điểm phần trăm coi là đi ngang — dao động ngày qua ngày luôn có, gọi nó là "tăng" thì
    // mũi tên đổi chiều mỗi lần đổi bộ lọc.
    if (Math.abs(delta) < 0.01) return {direction: 'flat', delta}

    return {direction: delta > 0 ? 'up' : 'down', delta}
}

/** Thứ tự hiển thị hạng — A trước, "chưa xếp hạng" cuối cùng. */
export const GRADE_ORDER = ['A', 'B', 'C', 'D', 'N']

/** Phải khớp mặc định `limit` của `attentionListOf` — dấu `+` sau số chỉ đúng khi danh sách đã đầy. */
export const ATTENTION_LIMIT = 5

/**
 * Câu mô tả dải phân bố hạng cho người đọc màn hình.
 *
 * ⚠️ Dải đó là HÌNH, và `title` trên từng khúc không tới được người dùng bàn phím. Không có câu này
 *    thì với họ khối Tổng quan chỉ là năm khúc màu không mang thông tin gì.
 */
export const gradeBarLabelOf = (counts, ranked) => {
    if (!ranked) return 'Chưa tài khoản nào xếp hạng được'

    return `Phân bố hạng trên ${ranked} tài khoản: ${GRADE_ORDER
        .filter((key) => counts[key] > 0)
        .map((key) => `${key === 'N' ? 'chưa xếp hạng' : `hạng ${key}`} ${counts[key]}`)
        .join(', ')}`
}

/**
 * Tỷ lệ → phần trăm chiều dài thanh, KẸP về 0–100.
 *
 * ⚠️ Trục của biểu đồ công đoạn cố định 0–100% chứ không co theo dữ liệu: hiệu suất là tỷ lệ mà
 *    100% mang nghĩa thật, còn trục co theo max biến một công đoạn 42% thành thanh gần kín khung.
 *    Công đoạn vượt 100% vì thế phải kẹp lại, và con số ở mũi thanh nói ra giá trị thật.
 */
export const barPercentOf = (ratio) => {
    if (typeof ratio !== 'number' || Number.isNaN(ratio)) return 0

    return Math.max(0, Math.min(100, ratio * 100))
}
